/**
 * Controles de toque — §15 da especificação.
 *
 * Metade ESQUERDA da tela é um joystick RELATIVO: o que importa é o delta
 * desde onde o dedo pousou, não a posição absoluta — assim o dedo não precisa
 * (nem tende a) cobrir o avião. Deadzone de 6 px no eixo X; o eixo Y pede um
 * arrasto maior porque acelerar/frear é decisão deliberada, e um polegar
 * guiando o avião balança verticalmente sem querer.
 *
 * Metade DIREITA é o botão de tiro (segurar = atirar; autofire de verdade
 * vira opção no M5 — no M1 o tiro ainda nem existe na simulação).
 *
 * Camada de INPUT, não de regra: aqui só se converte dedo em `Input`; quem
 * decide o que o input faz é o core.
 */

import type Phaser from 'phaser'
import { SCREEN, type Input } from '@pixelraid/core'

/** Deadzone horizontal (§15): 6 px lógicos. */
const DEADZONE_X = 6
/** Arrasto vertical deliberado para acelerar/frear. */
const DEADZONE_Y = 18

interface TrackedPointer {
  id: number
  originX: number
  originY: number
}

export class TouchControls {
  /** Um toque qualquer aconteceu — usado para reiniciar no game over. */
  tapped = false

  private steer: TrackedPointer | null = null
  private firePointers = new Set<number>()
  private moveX: -1 | 0 | 1 = 0
  private thrust: -1 | 0 | 1 = 0

  constructor(scene: Phaser.Scene) {
    // Até 2 dedos simultâneos: guiar e atirar.
    scene.input.addPointer(1)

    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.tapped = true
      if (pointer.x < SCREEN.WIDTH / 2) {
        if (this.steer === null) {
          this.steer = { id: pointer.id, originX: pointer.x, originY: pointer.y }
        }
      } else {
        this.firePointers.add(pointer.id)
      }
    })

    scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.steer?.id !== pointer.id) return
      const dx = pointer.x - this.steer.originX
      const dy = pointer.y - this.steer.originY
      this.moveX = dx > DEADZONE_X ? 1 : dx < -DEADZONE_X ? -1 : 0
      // Arrastar para CIMA (dy negativo) acelera, como o ↑ do teclado.
      this.thrust = dy < -DEADZONE_Y ? 1 : dy > DEADZONE_Y ? -1 : 0
    })

    const release = (pointer: Phaser.Input.Pointer): void => {
      if (this.steer?.id === pointer.id) {
        this.steer = null
        this.moveX = 0
        this.thrust = 0
      }
      this.firePointers.delete(pointer.id)
    }
    scene.input.on('pointerup', release)
    scene.input.on('pointerupoutside', release)
  }

  /** Contribuição do toque para o input do tick. */
  read(): Input {
    return { moveX: this.moveX, thrust: this.thrust, fire: this.firePointers.size > 0 }
  }

  /** Consome o registro de toque (borda, não nível). */
  consumeTap(): boolean {
    const was = this.tapped
    this.tapped = false
    return was
  }
}
